'use client';

import { FEATURES } from '@billing/plans';
import { useOptionalEntitlements } from '@components/billing/EntitlementsProvider';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { cn } from '@utils/cn';
import { getHref, ROUTE } from '@utils/route-utils';
import { Sparkles } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { FC, useEffect, useState } from 'react';

export type GenerateImageKindHint = 'product' | 'scene';

type Candidate = {
  fileId: string;
  url: string;
  width: number | null;
  height: number | null;
  contentType: string;
};

type Usage = { used: number; limit: number | null; configured: boolean };

type PanelState = 'idle' | 'generating' | 'review' | 'accepting' | 'error';

type GenerateImagePanelProps = {
  organizationId: string;
  targetType: 'menus' | 'recipes' | 'ingredients';
  targetId: string;
  canEdit: boolean;
  kindHint?: GenerateImageKindHint;
  maxCandidates?: number;
  /** Called after a candidate is attached to the target. */
  onAccepted?: (fileId: string) => void;
  /** `bare` drops the outline when the panel already sits in a sheet. */
  appearance?: 'outlined' | 'bare';
};

const DEFAULT_MAX_CANDIDATES = 4;

/**
 * Monthly AI image usage for the org. `configured: false` means the image
 * provider is not set up, so generation affordances should stay hidden.
 */
export function useImageGenerationUsage(organizationId: string) {
  const [usage, setUsage] = useState<Usage | null>(null);

  const loadUsage = async () => {
    try {
      const response = await fetch(
        `/api/image-generations/usage?organizationId=${encodeURIComponent(organizationId)}`
      );
      if (!response.ok) return;
      const data = (await response.json()) as Usage;
      setUsage(data);
    } catch {
      // Usage is advisory only; a failed lookup just hides the counter.
    }
  };

  useEffect(() => {
    void loadUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  return { usage, setUsage, loadUsage };
}

const GenerateImagePanel: FC<GenerateImagePanelProps> = ({
  organizationId,
  targetType,
  targetId,
  canEdit,
  kindHint,
  maxCandidates = DEFAULT_MAX_CANDIDATES,
  onAccepted,
  appearance = 'outlined'
}) => {
  const t = useTranslations('image-gen');
  const entitlements = useOptionalEntitlements();
  const { usage, setUsage, loadUsage } =
    useImageGenerationUsage(organizationId);
  const [state, setState] = useState<PanelState>('idle');
  const [hint, setHint] = useState('');
  const [count, setCount] = useState(1);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [error, setError] = useState<string | null>(null);

  const locked = entitlements ? !entitlements.has(FEATURES.AiImages) : false;
  const atLimit = usage?.limit != null && usage.used >= usage.limit;

  if (!canEdit || !usage?.configured) {
    return null;
  }

  if (locked) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-border p-3">
        <p className="text-meta text-muted-foreground">{t('not-entitled')}</p>
        <Button asChild variant="outline" size="sm">
          <Link href={getHref(organizationId, [ROUTE.UPGRADE])}>
            {t('generate')}
          </Link>
        </Button>
      </div>
    );
  }

  const generate = async (existingHint = hint) => {
    setState('generating');
    setError(null);
    try {
      const response = await fetch('/api/image-generations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          targetType,
          targetId,
          count,
          hint: existingHint || undefined
        })
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;
        if (response.status === 402) {
          setError(t('error-limit'));
        } else if (response.status === 429) {
          setError(t('error-rate-limit'));
        } else {
          setError(t('error-provider'));
        }
        setState('error');
        console.error('Image generation failed', body?.error);
        return;
      }
      const data = (await response.json()) as {
        generationId: string;
        candidates: Candidate[];
        usage: { used: number; limit: number | null };
      };
      setGenerationId(data.generationId);
      setCandidates(data.candidates);
      setUsage(current => ({
        used: data.usage.used,
        limit: data.usage.limit,
        configured: current?.configured ?? true
      }));
      setState('review');
    } catch {
      setError(t('error-provider'));
      setState('error');
    }
  };

  const accept = async (fileId: string) => {
    if (!generationId) return;
    setState('accepting');
    try {
      const response = await fetch(
        `/api/image-generations/${generationId}/accept`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ organizationId, fileId })
        }
      );
      if (!response.ok) {
        setError(t('error-provider'));
        setState('error');
        return;
      }
      resetToIdle();
      onAccepted?.(fileId);
    } catch {
      setError(t('error-provider'));
      setState('error');
    }
  };

  const discard = async () => {
    if (!generationId) {
      resetToIdle();
      return;
    }
    try {
      await fetch(
        `/api/image-generations/${generationId}?organizationId=${encodeURIComponent(organizationId)}`,
        { method: 'DELETE' }
      );
    } finally {
      resetToIdle();
    }
  };

  const regenerate = async () => {
    await discard();
    void generate(hint);
  };

  function resetToIdle() {
    setGenerationId(null);
    setCandidates([]);
    setState('idle');
    void loadUsage();
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-3',
        appearance === 'outlined' && 'rounded-md border border-border p-3'
      )}
    >
      {state === 'idle' || state === 'error' ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={hint}
              onChange={event => setHint(event.target.value)}
              placeholder={t('hint-placeholder')}
              className="min-w-48 flex-1"
              maxLength={500}
            />
            <div
              role="group"
              aria-label={t('count')}
              className="flex items-center gap-1"
            >
              {Array.from(
                { length: Math.min(maxCandidates, DEFAULT_MAX_CANDIDATES) },
                (_, index) => index + 1
              ).map(value => (
                <Button
                  key={value}
                  type="button"
                  variant={count === value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCount(value)}
                >
                  {value}
                </Button>
              ))}
            </div>
            <Button
              type="button"
              onClick={() => void generate()}
              disabled={atLimit}
            >
              <Sparkles className="size-4" />
              {t('generate')}
            </Button>
          </div>
          {kindHint === 'product' ? (
            <p className="text-meta text-muted-foreground">
              {t('product-note')}
            </p>
          ) : null}
          {usage ? (
            <p className="text-meta text-muted-foreground">
              {usage.limit == null
                ? t('usage-unlimited', { used: usage.used })
                : t('usage', { used: usage.used, limit: usage.limit })}
            </p>
          ) : null}
          {atLimit ? (
            <p className="text-meta text-destructive">
              {t('limit-reached')}{' '}
              <Link
                className="underline"
                href={getHref(organizationId, [ROUTE.SETTINGS])}
              >
                {t('generate')}
              </Link>
            </p>
          ) : null}
          {error ? (
            <p className="text-meta text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}

      {state === 'generating' ? (
        <p className="text-meta text-muted-foreground" role="status">
          {t('generating')}
        </p>
      ) : null}

      {state === 'review' || state === 'accepting' ? (
        <div className="flex flex-col gap-3">
          <p className="text-label">{t('candidates-title')}</p>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {candidates.map(candidate => (
              <li
                key={candidate.fileId}
                className="flex flex-col gap-2"
                style={{
                  backgroundImage:
                    'linear-gradient(45deg, var(--muted) 25%, transparent 25%), linear-gradient(-45deg, var(--muted) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, var(--muted) 75%), linear-gradient(-45deg, transparent 75%, var(--muted) 75%)',
                  backgroundSize: '16px 16px',
                  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0'
                }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={candidate.url}
                  alt={t('candidates-title')}
                  className="aspect-square w-full rounded-md border border-border object-contain"
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={state === 'accepting'}
                  onClick={() => void accept(candidate.fileId)}
                >
                  {t('use-image')}
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={state === 'accepting'}
              onClick={() => void regenerate()}
            >
              {t('regenerate')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={state === 'accepting'}
              onClick={() => void discard()}
            >
              {t('discard')}
            </Button>
          </div>
          <p className="text-meta text-muted-foreground">{t('expires-note')}</p>
        </div>
      ) : null}
    </div>
  );
};

export default GenerateImagePanel;
